import { AssignStudioConfigValuesType } from '@dtx-company/app/shared/code/src/types/upgrade.types'
import { GetUserInfoResponseType } from '@dtx-company/app/shared/code/src/types/user.types'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { UNIFIED_ASSET_MANAGEMENT } from '@dtx-company/true-common/src/constants/featureFlags'
import { USER_INFO_QUERY } from '@dtx-company/app/shared/code/src/gql/queries/user'
import { callAssignConfig } from '@dtx-company/app/shared/code/src/utils/assign-configuration'
import { isValidDynamicRoute } from '@dtx-company/true-common/src/utils/urls/isValidDynamicRoute'
import { reverseEnumLookup } from '@dtx-company/true-common/src/utils/types/reverseEnumLookup'
import { useCallback, useEffect, useState } from 'react'
import { useFlowFeature } from '../useFlowFeature/useFlowFeature'
import { useFlowRouter } from '../../hooks/useFlowRouter'
import qs from 'query-string'
import useSWR from 'swr'

const getValidRedirectDestination = (pathname: string, fallback = Routes.CODES): string => {
  const isValidRoute = Boolean(reverseEnumLookup(Routes, pathname)) || isValidDynamicRoute(pathname)
  return isValidRoute ? pathname : fallback
}

export const useHandleFlowcodeUserRedirect = (): (() => Promise<void>) => {
  const router = useFlowRouter()
  const [isUnifiedAssetManagementEnabled] = useFlowFeature(UNIFIED_ASSET_MANAGEMENT, {
    autoUpdate: true
  })
  const productId = router.getString('productId')
  const redirectTo = router.getString('redirectTo')
  const decodedRedirectTo = redirectTo ? decodeURIComponent(redirectTo) : ''
  const { url: redirectToUrl, query } = qs.parseUrl(decodedRedirectTo)
  const [productPageRedirection, setProductPageRedirection] = useState(false)
  const { data: userInfoData } = useSWR<GetUserInfoResponseType>(
    () => (productPageRedirection ? USER_INFO_QUERY : null),
    {
      revalidateOnFocus: false
    }
  )

  const isSourceStoreTemplates = redirectToUrl.startsWith('/store/templates')

  useEffect(() => {
    if (!isSourceStoreTemplates && productId && userInfoData) {
      const hasNoCode = userInfoData.me.flowcodeCount === 0
      hasNoCode
        ? router.push({ pathname: Routes.CREATE_FLOWCODE, query: { productId } })
        : router.push({
            pathname: Routes.PRINT_STORE_LANDING_PRODUCT_DETAILS,
            query: {
              productId
            }
          })
    }
  }, [userInfoData, productId, router, isSourceStoreTemplates])

  const handleFlowcodeUserRedirect = useCallback(async (): Promise<void> => {
    const params = router.query
    const optionParams = { option: params.option }
    const configInput: AssignStudioConfigValuesType = {
      configId: String(params.configid)
    }
    const scanToOwnParams = { batchId: params.batchId }
    const redirectTo = params.redirectTo?.toString()
    const isRedirectToStore = decodedRedirectTo?.startsWith(Routes.PRINT_STORE_LANDING_PAGE)

    if (params.option && params.configid) {
      await callAssignConfig(configInput)
      router.push({
        pathname: Routes.CREATE_FLOWCODE,
        query: optionParams
      })
    } else if (params.batchId && params.source === 'create') {
      router.push({
        pathname: Routes.CODES,
        query: {
          batchId: params.batchId
        }
      })
    } else if (params.batchId && !redirectTo) {
      router.push({
        pathname: Routes.STO_GENERIC,
        query: scanToOwnParams
      })
    } else if (!isSourceStoreTemplates && params.productId) {
      setProductPageRedirection(true)
    } else if (decodedRedirectTo) {
      router.push({
        pathname: isRedirectToStore ? redirectToUrl : getValidRedirectDestination(redirectToUrl),
        query: qs.stringify(query)
      })
    } else {
      router.push(isUnifiedAssetManagementEnabled ? Routes.ASSETS : Routes.CODES)
    }
  }, [
    router,
    decodedRedirectTo,
    isSourceStoreTemplates,
    redirectToUrl,
    query,
    isUnifiedAssetManagementEnabled
  ])

  return handleFlowcodeUserRedirect
}
